// Step 7.1 commit 2 / AC244+AC246: single source of truth for the
// next Mon/Wed delivery dates and their cutoffs. Every customer-
// facing surface (Dashboard hero, top teal banner, cart drawer,
// checkout, Power Buyer slide-to-order) consumes this module so
// there is exactly one notion of "today's available dates."
//
// The Step 7 module produced wrong dates in two ways:
// 1. Cart drawer iterated UTC weekdays. When a user opened the app
//    after ~8pm ET (= midnight UTC next day), the "today" anchor
//    rolled forward and the loop emitted Tue/Sun/Tue instead of
//    Mon/Wed.
// 2. The legacy lib/delivery/schedule.ts approximated the cutoff as
//    "delivery_noon_UTC - 10h" = 22:00 ET, which only matches the
//    spec by coincidence in summer and breaks under EST.
//
// This module fixes both by iterating ET calendar days (via
// Intl.DateTimeFormat) and locking the cutoff to 14:00 UTC of the
// cutoff day. 14:00 UTC reads as 10am EDT in summer and 9am EST in
// winter - both align with spec §6 word-for-word.

import {
  ET_TZ,
  CUTOFF_UTC_HOUR,
  MS_PER_DAY,
  type EtDay,
  etDayOf,
  etDayToAnchor,
  addEtDays,
  etWeekday,
  isoFromEtDay,
  etDayFromIso,
} from "./et-days";

// Re-exported for existing importers (behavior unchanged).
export { isoFromEtDay, etDayFromIso };

export type DeliveryWeekday = 1 | 3; // Monday, Wednesday

export type DeliveryOption = {
  isoDate: string; // YYYY-MM-DD, ET calendar day
  date: Date; // ET-day anchor at noon UTC
  cutoff: Date; // 14:00 UTC of the cutoff day
  weekday: DeliveryWeekday;
};

function cutoffForEtDay(et: EtDay): Date {
  // Locked Jun 18 by Alexis: both Mon + Wed deliveries cut off 5 ET
  // calendar days before the delivery date at 10am ET.
  //   Monday delivery    -> prior Wednesday 10am ET
  //   Wednesday delivery -> prior Friday    10am ET
  // (Earlier copy used weekday===1?5:6 which put Wed deliveries on the
  // prior Thursday.)
  const offset = 5;
  const cutoffEtAnchor = new Date(etDayToAnchor(et).getTime() - offset * MS_PER_DAY);
  return new Date(
    Date.UTC(
      cutoffEtAnchor.getUTCFullYear(),
      cutoffEtAnchor.getUTCMonth(),
      cutoffEtAnchor.getUTCDate(),
      CUTOFF_UTC_HOUR,
      0,
      0,
      0,
    ),
  );
}

export function cutoffForIso(iso: string): Date {
  const et = etDayFromIso(iso);
  const wd = etWeekday(et);
  if (wd !== 1 && wd !== 3) {
    throw new Error(`No cutoff for non-Mon/Wed delivery ${iso} (ET weekday ${wd})`);
  }
  return cutoffForEtDay(et);
}

export function getNextDeliveries(
  now: Date = new Date(),
  options: {
    count?: number;
    isBlocked?: (iso: string) => boolean;
  } = {},
): DeliveryOption[] {
  const count = options.count ?? 3;
  const isBlocked = options.isBlocked ?? (() => false);
  const startEt = etDayOf(now);
  const out: DeliveryOption[] = [];
  for (let offset = 0; offset < 120 && out.length < count; offset++) {
    const candidate = addEtDays(startEt, offset);
    const wd = etWeekday(candidate);
    if (wd !== 1 && wd !== 3) continue;
    const iso = isoFromEtDay(candidate);
    if (isBlocked(iso)) continue;
    const cutoff = cutoffForEtDay(candidate);
    if (cutoff.getTime() <= now.getTime()) continue;
    out.push({
      isoDate: iso,
      date: etDayToAnchor(candidate),
      cutoff,
      weekday: wd as DeliveryWeekday,
    });
  }
  return out;
}

export function getNextDelivery(
  now: Date = new Date(),
  options?: { isBlocked?: (iso: string) => boolean },
): DeliveryOption | null {
  const list = getNextDeliveries(now, { count: 1, isBlocked: options?.isBlocked });
  return list[0] ?? null;
}

// For the catalog top-banner progress meter: returns the open delivery
// window's previous cutoff (= when this window started) along with the
// next delivery. The previous delivery is the most recent Mon/Wed
// strictly before `current`.
export function getCurrentWindow(
  now: Date = new Date(),
  options?: { isBlocked?: (iso: string) => boolean },
): { current: DeliveryOption; previousCutoff: Date } | null {
  const current = getNextDelivery(now, options);
  if (!current) return null;
  // The previous Mon/Wed is either 2 or 5 days earlier in ET:
  // Wed delivery -> previous Mon (2 days before)
  // Mon delivery -> previous Wed (5 days before)
  const offsetDays = current.weekday === 1 ? 5 : 2;
  const prevEt = addEtDays(etDayFromIso(current.isoDate), -offsetDays);
  const previousCutoff = cutoffForEtDay(prevEt);
  return { current, previousCutoff };
}

// Format helpers - keeps every surface visually consistent.
const FMT_FULL_DATE = new Intl.DateTimeFormat("en-US", {
  timeZone: ET_TZ,
  weekday: "long",
  month: "short",
  day: "numeric",
});

const FMT_DAY_NAME = new Intl.DateTimeFormat("en-US", {
  timeZone: ET_TZ,
  weekday: "short",
});

const FMT_CUTOFF = new Intl.DateTimeFormat("en-US", {
  timeZone: ET_TZ,
  weekday: "short",
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
  timeZoneName: "short",
});

export function formatDeliveryLabel(opt: DeliveryOption): string {
  return FMT_FULL_DATE.format(opt.date);
}

export function formatDeliveryShort(opt: DeliveryOption): string {
  return FMT_FULL_DATE.format(opt.date);
}

export function formatWeekdayShort(opt: DeliveryOption): string {
  return FMT_DAY_NAME.format(opt.date);
}

export function formatCutoffLabel(opt: DeliveryOption): string {
  return `Order by ${FMT_CUTOFF.format(opt.cutoff)}`;
}

export function formatCutoffPlain(cutoff: Date): string {
  return FMT_CUTOFF.format(cutoff);
}
